/**
 * Minimal, typed form state for create/edit dialogs.
 *
 * Deliberately small: a `values` object, a `set` per field, client-side zod
 * validation on submit, and server-side field errors (RFC 9457 `errors[]`)
 * merged into the same `errors` map so inputs render a single message.
 */
import { useCallback, useMemo, useState } from "react";
import type { z } from "zod";

import { ApiError } from "@/api/problem";

/** Form API returned by `useForm`. */
export interface FormState<T extends Record<string, unknown>> {
  values: T;
  errors: Partial<Record<keyof T | "_", string>>;
  dirty: boolean;
  submitting: boolean;
  /** Set a single field. */
  set: <K extends keyof T>(key: K, value: T[K]) => void;
  /** Merge several fields. */
  patch: (patch: Partial<T>) => void;
  /** Replace values (e.g. after loading an entity) and clear dirty state. */
  reset: (values?: T) => void;
  /**
   * Validate then run `action`. Server `ApiError`s are mapped into `errors`;
   * unknown errors land under `_`. Resolves to `true` when `action` succeeded.
   */
  submit: (action: (values: T) => Promise<unknown>) => Promise<boolean>;
  /** Props helper for inputs: `id`, `value`, `onChange`, `aria-invalid`. */
  field: (key: keyof T) => FieldBinding;
}

/** Binding returned by `form.field(key)` for text-like inputs. */
export interface FieldBinding {
  id: string;
  name: string;
  value: string;
  "aria-invalid": boolean | undefined;
  onChange: (e: { target: { value: string } }) => void;
}

/** Options for `useForm`. */
export interface UseFormOptions<T extends Record<string, unknown>> {
  initial: T;
  /** Optional zod schema applied before `submit` runs the action. */
  schema?: z.ZodType;
  /** Prefix for generated input ids (defaults to `f`). */
  idPrefix?: string;
}

/** Convert a zod error into a per-field message map. */
export function zodErrors<T>(error: z.ZodError): Partial<Record<keyof T | "_", string>> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = String(issue.path[0] ?? "_");
    out[key] ??= issue.message;
  }
  return out as Partial<Record<keyof T | "_", string>>;
}

/** Create form state. */
export function useForm<T extends Record<string, unknown>>({
  initial,
  schema,
  idPrefix = "f",
}: UseFormOptions<T>): FormState<T> {
  const [values, setValues] = useState<T>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof T | "_", string>>>({});
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = useCallback(<K extends keyof T>(key: K, value: T[K]): void => {
    setValues((v) => ({ ...v, [key]: value }));
    setErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
    setDirty(true);
  }, []);

  const patch = useCallback((p: Partial<T>): void => {
    setValues((v) => ({ ...v, ...p }));
    setDirty(true);
  }, []);

  const reset = useCallback(
    (next?: T): void => {
      setValues(next ?? initial);
      setErrors({});
      setDirty(false);
    },
    [initial],
  );

  const submit = useCallback(
    async (action: (values: T) => Promise<unknown>): Promise<boolean> => {
      if (schema) {
        const parsed = schema.safeParse(values);
        if (!parsed.success) {
          setErrors(zodErrors<T>(parsed.error));
          return false;
        }
      }
      setSubmitting(true);
      setErrors({});
      try {
        await action(values);
        setDirty(false);
        return true;
      } catch (err) {
        if (err instanceof ApiError) {
          const fieldErrors = err.fieldErrors();
          setErrors(
            Object.keys(fieldErrors).length
              ? (fieldErrors as Partial<Record<keyof T | "_", string>>)
              : ({ _: err.message } as Partial<Record<keyof T | "_", string>>),
          );
        } else {
          setErrors({ _: err instanceof Error ? err.message : String(err) } as Partial<
            Record<keyof T | "_", string>
          >);
        }
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [schema, values],
  );

  const field = useCallback(
    (key: keyof T): FieldBinding => ({
      id: `${idPrefix}-${String(key)}`,
      name: String(key),
      value: values[key] === null || values[key] === undefined ? "" : String(values[key]),
      "aria-invalid": errors[key] ? true : undefined,
      onChange: (e): void => set(key, e.target.value as T[keyof T]),
    }),
    [values, errors, set, idPrefix],
  );

  return useMemo(
    () => ({ values, errors, dirty, submitting, set, patch, reset, submit, field }),
    [values, errors, dirty, submitting, set, patch, reset, submit, field],
  );
}

/** Turn an empty string into `null` (for optional API fields). */
export function nullable(value: string | null | undefined): string | null {
  return value === undefined || value === null || value.trim() === "" ? null : value;
}

/** Parse a numeric input; empty → `null`. */
export function numberOrNull(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

/** Convert a `datetime-local` input value into an ISO string (or null). */
export function isoOrNull(value: string | null | undefined): string | null {
  return value ? new Date(value).toISOString() : null;
}
